import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import SpotCheckTemplatesView, {
  type SpotCheckCategoryKey,
} from '../../Aspen/SpotCheckTemplatesView';
import { type CategorizedEventSpotChecks } from '../../hooks/useAiNotifcations';
import { type UseEventSpotCheckStructuredOutput } from '../../hooks/useEventSpotCheckStructuredOutput';
import { type UseSpotCheckForm } from '../../hooks/useSpotCheckForm';
import { type UseSpotCheckPreview } from '../../hooks/useSpotCheckPreview';
import { type CategorizedSpotChecks } from '../../hooks/useSpotChecks';
import { type UseFormattedSpotCheckStructuredOutput } from '../../hooks/useSpotCheckStructuredOutput';
import SpotCheckFormView from '../SpotCheckFormView/Aspen/SpotCheckFormView';

interface SpotCheckMainContentProps {
  view: 'templates' | 'spot-check-form';
  setView: React.Dispatch<React.SetStateAction<SpotCheckView>>;
  categorizedSpotChecks: CategorizedSpotChecks | undefined;
  categorizedEventSpotChecks: CategorizedEventSpotChecks;
  setSpotCheck: React.Dispatch<React.SetStateAction<ScheduledActivityEntity | undefined>>;
  setEventSpotCheck: React.Dispatch<React.SetStateAction<AiNotification | undefined>>;
  spotCheck: ScheduledActivityEntity | undefined;
  eventSpotCheck: AiNotification | undefined;
  spotCheckForm: UseSpotCheckForm;
  selectedCategoryKey: string | null;
  setSelectedCategoryKey: (key: string | null) => void;
  selectedSpotCheckCategoryKey: SpotCheckCategoryKey | null;
  setSelectedSpotCheckCategoryKey: (key: SpotCheckCategoryKey | null) => void;
  spotCheckSearchPrompt: string;
  setSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  eventSpotCheckSearchPrompt: string;
  setEventSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  spotCheckPreview: UseSpotCheckPreview;
  createSpotCheckPreview: () => void;
  spotCheckStructuredOutput: UseFormattedSpotCheckStructuredOutput;
  eventSpotCheckStructuredOutput: UseEventSpotCheckStructuredOutput;
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit'>>;
  handleGoBack: () => void;
  excludedCameraIds: string[];
  setExcludedCameraIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCameraIndex: number;
  setSelectedCameraIndex: React.Dispatch<React.SetStateAction<number>>;
  timezoneId: string;
  formMode: 'create' | 'edit';
  setIsExistingSpotCheck: React.Dispatch<React.SetStateAction<boolean>>;
  isExistingSpotCheck: boolean;
  enterCameraSelectMode: () => void;
  sentEventSpotCheckRequest: boolean;
  eventSpotCheckAnswer: string;
  eventImagePreviewUrls: string[];
  totalEventsInLast30Days: number | undefined;
  isEventsEstimateBelowLimit: boolean;
  eventsMonthlyQuotaLimit: number | undefined;
  reportEvent: IEvent | undefined;
}

export type SpotCheckView = 'templates' | 'spot-check-form';

export default function SpotCheckMainContent(props: SpotCheckMainContentProps) {
  const {
    view,
    setView,
    categorizedSpotChecks,
    categorizedEventSpotChecks,
    spotCheck,
    eventSpotCheck,
    setSpotCheck,
    setEventSpotCheck,
    spotCheckForm,
    selectedCategoryKey,
    setSelectedCategoryKey,
    selectedSpotCheckCategoryKey,
    setSelectedSpotCheckCategoryKey,
    spotCheckSearchPrompt,
    setSpotCheckSearchPrompt,
    eventSpotCheckSearchPrompt,
    setEventSpotCheckSearchPrompt,
    spotCheckPreview,
    createSpotCheckPreview,
    spotCheckStructuredOutput,
    eventSpotCheckStructuredOutput,
    setFormMode,
    handleGoBack,
    excludedCameraIds,
    setExcludedCameraIds,
    selectedCameraIndex,
    setSelectedCameraIndex,
    timezoneId,
    formMode,
    setIsExistingSpotCheck,
    isExistingSpotCheck,
    enterCameraSelectMode,
    eventImagePreviewUrls,
    sentEventSpotCheckRequest,
    eventSpotCheckAnswer,
    totalEventsInLast30Days,
    isEventsEstimateBelowLimit,
    eventsMonthlyQuotaLimit,
    reportEvent,
  } = props;

  const views: Record<SpotCheckView, React.ReactNode> = {
    templates: (
      <SpotCheckTemplatesView
        setView={setView}
        categorizedSpotChecks={categorizedSpotChecks}
        categorizedEventSpotChecks={categorizedEventSpotChecks}
        setSpotCheck={setSpotCheck}
        setEventSpotCheck={setEventSpotCheck}
        selectedCategoryKey={selectedCategoryKey}
        setSelectedCategoryKey={setSelectedCategoryKey}
        selectedSpotCheckCategoryKey={selectedSpotCheckCategoryKey}
        setSelectedSpotCheckCategoryKey={setSelectedSpotCheckCategoryKey}
        spotCheck={spotCheck}
        eventSpotCheck={eventSpotCheck}
        spotCheckSearchPrompt={spotCheckSearchPrompt}
        setSpotCheckSearchPrompt={setSpotCheckSearchPrompt}
        eventSpotCheckSearchPrompt={eventSpotCheckSearchPrompt}
        setEventSpotCheckSearchPrompt={setEventSpotCheckSearchPrompt}
        setFormMode={setFormMode}
        setIsExistingSpotCheck={setIsExistingSpotCheck}
      />
    ),
    'spot-check-form': (
      <SpotCheckFormView
        spotCheck={spotCheck}
        eventSpotCheck={eventSpotCheck}
        spotCheckForm={spotCheckForm}
        spotCheckPreview={spotCheckPreview}
        createSpotCheckPreview={createSpotCheckPreview}
        spotCheckStructuredOutput={spotCheckStructuredOutput}
        eventSpotCheckStructuredOutput={eventSpotCheckStructuredOutput}
        handleGoBack={handleGoBack}
        excludedCameraIds={excludedCameraIds}
        setExcludedCameraIds={setExcludedCameraIds}
        selectedCameraIndex={selectedCameraIndex}
        setSelectedCameraIndex={setSelectedCameraIndex}
        timezoneId={timezoneId}
        formMode={formMode}
        isExistingSpotCheck={isExistingSpotCheck}
        enterCameraSelectMode={enterCameraSelectMode}
        eventImagePreviewUrls={eventImagePreviewUrls}
        sentEventSpotCheckRequest={sentEventSpotCheckRequest}
        eventSpotCheckAnswer={eventSpotCheckAnswer}
        totalEventsInLast30Days={totalEventsInLast30Days}
        isEventsEstimateBelowLimit={isEventsEstimateBelowLimit}
        eventsMonthlyQuotaLimit={eventsMonthlyQuotaLimit}
        reportEvent={reportEvent}
      />
    ),
  };

  return <div className="tw h-full min-h-0">{views[view]}</div>;
}
